import React from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardActionArea,
  CardContent,
  Button,
  Stack,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DashboardIcon from "@mui/icons-material/Dashboard";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import AssessmentIcon from "@mui/icons-material/Assessment";

const spifsp = process.env.REACT_APP_SPIFSP;
const console2 = process.env.REACT_APP_CONSOLE2;
const dps = process.env.REACT_APP_DPS;

const APPS = [
  {
    title: "IFCC Console",
    description: "Classic dashboards and reports, kept for continuity.",
    icon: <DashboardIcon sx={{ fontSize: 32 }} />,
    color: "#0563C1",
    bgColor: "#EDF3FB",
    to: "/sno",
  },
  {
    title: "PCC Console",
    description: "Real-time orchestration, exceptions and alerting.",
    icon: <AssessmentIcon sx={{ fontSize: 32 }} />,
    color: "#0563C1",
    bgColor: "#EDF3FB",
    to: console2,
    enabled: !!console2,
  },
  {
    title: "SPI and FSP",
    description: "Supply and finished-product planning across the network.",
    icon: <Inventory2Icon sx={{ fontSize: 32 }} />,
    color: "#0563C1",
    bgColor: "#EDF3FB",
    to: spifsp,
    enabled: !!spifsp,
  },
  {
    title: "DPS",
    description: "Deployment and replenishment across distribution.",
    icon: <LocalShippingIcon sx={{ fontSize: 32 }} />,
    color: "#0563C1",
    bgColor: "#EDF3FB",
    to: dps,
    enabled: !!dps,
  },
];

function StatusPill() {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        px: 2,
        py: 1,
        borderRadius: 3,
        border: "1px solid #D0E1F0",
        bgcolor: "#F0F6FB",
        boxShadow: "0 2px 8px rgba(5, 99, 193, 0.08)",
      }}
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          bgcolor: "#10B981",
          boxShadow: "0 0 0 3px rgba(16, 185, 129, 0.2)",
        }}
      />
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          color: "#0563C1",
          whiteSpace: "nowrap",
        }}
      >
        All Systems Operational
      </Typography>
    </Box>
  );
}

function AppCard({ app }) {
  const isDisabled = !app.to;

  const handleClick = () => {
    if (!app.to) {
      console.warn(`${app.title} URL is not configured`);
      return;
    }
    if (app.to.startsWith("http")) {
      window.location.href = app.to;
      return;
    }
    sessionStorage.setItem("internalAppTarget", app.to);
    window.location.reload();
  };

  return (
    <Card
      sx={{
        opacity: isDisabled ? 0.6 : 1,
        pointerEvents: isDisabled ? "none" : "auto",
        border: "1px solid #E8EDF5",
        borderRadius: 2,
        boxShadow: "0 2px 8px rgba(5, 99, 193, 0.06)",
        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        width: "100%",
        maxWidth: "100%",
        height: "100%",
        minHeight: 180,
        boxSizing: "border-box",
        bgcolor: "#FFFFFF",
        "&:hover": {
          borderColor: "#0563C1",
          boxShadow: "0 8px 20px rgba(5, 99, 193, 0.12)",
          transform: "translateY(-2px)",
        },
      }}
    >
      <CardActionArea
        onClick={handleClick}
        sx={{
          height: "100%",
          width: "100%",
          p: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          boxSizing: "border-box",
        }}
      >
        <CardContent sx={{ p: 0, width: "100%", "&:last-child": { pb: 0 } }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2, height: "100%", width: "100%" }}>
            {/* Icon Container */}
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: 2.5,
                bgcolor: app.bgColor,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: app.color,
                boxShadow: "0 4px 12px rgba(5, 99, 193, 0.1)",
              }}
            >
              {app.icon}
            </Box>

            {/* Title */}
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                letterSpacing: "-0.02em",
                color: "#1F2937",
                fontSize: "1.1rem",
              }}
            >
              {app.title}
            </Typography>

            {/* Description */}
            <Typography
              variant="body2"
              sx={{
                color: "#6B7280",
                lineHeight: 1.5,
                flex: 1,
                fontSize: "0.9rem",
                fontWeight: 500,
              }}
            >
              {app.description}
            </Typography>

            {/* Button */}
            <Box sx={{ mt: "auto", pt: 0.5 }}>
              <Button
                component="span"
                variant="outlined"
                endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: 2,
                  borderColor: "#0563C1",
                  color: "#0563C1",
                  fontSize: "0.9rem",
                  py: 1,
                  px: 2,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: "#0563C1",
                    borderColor: "#0563C1",
                    color: "#fff",
                    boxShadow: "0 4px 12px rgba(5, 99, 193, 0.3)",
                  },
                }}
              >
                {isDisabled ? "Not Configured" : "Open"}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

export default function LandingPage({ setFlag }) {
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F8FAFB", pt: 2, pb: 2, width: "100%" }}>
      <Container
        maxWidth={false}
        disableGutters
        sx={{ px: { xs: 2, sm: 3, md: 4 }, width: "100%", maxWidth: "100%", boxSizing: "border-box" }}
      >
        {/* Header Section */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#0563C1",
                mb: 0.5,
              }}
            >
              Supply Chain · Operations Portal
            </Typography>
            <Typography
              component="h1"
              sx={{
                fontSize: { xs: 28, sm: 36, md: 40 },
                fontWeight: 800,
                letterSpacing: "-0.02em",
                lineHeight: 1.1,
                color: "#1F2937",
              }}
            >
              Command Center
            </Typography>
          </Box>
          <StatusPill />
        </Stack>

        {/* Divider */}
        <Box
          sx={{
            height: "1px",
            background: "linear-gradient(90deg, #E8EDF5 0%, #D0E1F0 50%, #E8EDF5 100%)",
            my: 1.5,
          }}
        />

        {/* Cards Grid — equal-width 2×2, full page width */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(2, 1fr)",
            },
            gap: 3,
            maxWidth: "1080px",
            mx: "auto",
            mt: 4,
          }}
        >
          {APPS.map((app) => (
            <Box key={app.title} sx={{ display: "flex", width: "100%", height: "100%" }}>
              <AppCard app={app} />
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
}
